import { withRequestDeadline, throwIfAborted } from '../sync/requestDeadline'
import { secureStorage } from '../crypto/secureStorage'
import { getPlatformBridge } from '../platform/platformBridge'
import { syncEngine } from '../sync/syncEngine'
import { ENTITLEMENT_SIGNING_KEYS } from './entitlementPublicKey'
import { OtherAccountWritingError } from './otherAccountWriting'

type Platform = 'desktop' | 'android' | 'web'
export interface CertificateEnvelope { payload: string; signature: string; keyId: keyof typeof ENTITLEMENT_SIGNING_KEYS }
interface CertificatePayload {
  accountId: string
  deviceId: string
  platform: Platform
  deviceFingerprint?: string
  issuedAt: string
  /** Until when the sync subscription is paid; null once it has lapsed. */
  syncUntil?: string | null
  /** Certificates issued before local licenses became permanent expired at this date. */
  validUntil?: string
}

/**
 * What a verified certificate grants this device. A valid certificate is a
 * permanent license to use the app locally; sync is paid separately.
 */
export interface OfflineEntitlement {
  syncUntil: string | null
}

function toEntitlement(payload: CertificatePayload): OfflineEntitlement {
  return { syncUntil: payload.syncUntil !== undefined ? payload.syncUntil : (payload.validUntil ?? null) }
}

function fromBase64Url(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const binary = atob(normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '='))
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index)
  }
  return bytes
}

async function sha256(value: string): Promise<string> {
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)))
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

async function deviceIdentity(): Promise<{ platform: Platform; fingerprint?: string; label: string }> {
  const isTauri = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window
  if (!isTauri) return { platform: 'web', label: 'Web browser' }

  const bridge = getPlatformBridge()
  const desktop = await bridge.isDesktopPlatform()
  const label = (await bridge.getHostname()) || (desktop ? 'Desktop' : 'Android')
  if (desktop) {
    const raw = await bridge.getDeviceFingerprint()
    if (!raw) throw new Error('This computer does not expose a stable device identifier.')
    return { platform: 'desktop', fingerprint: await sha256(raw), label }
  }
  // The Android WebView's local storage is app-private. A first-run random ID
  // is therefore the app-scoped identifier described by the entitlement design.
  return { platform: 'android', fingerprint: await secureStorage.getOrCreateInstallationId(), label }
}

async function verify(envelope: CertificateEnvelope, identity: Awaited<ReturnType<typeof deviceIdentity>>): Promise<CertificatePayload> {
  const jwk = ENTITLEMENT_SIGNING_KEYS[envelope.keyId]
  if (!jwk) throw new Error('Unknown entitlement signing key.')
  const key = await crypto.subtle.importKey('jwk', jwk, { name: 'Ed25519' }, false, ['verify'])
  const valid = await crypto.subtle.verify(
    'Ed25519', key, fromBase64Url(envelope.signature), new TextEncoder().encode(envelope.payload),
  )
  if (!valid) throw new Error('The entitlement certificate signature is invalid.')
  const payload = JSON.parse(new TextDecoder().decode(fromBase64Url(envelope.payload))) as CertificatePayload
  if (typeof payload.accountId !== 'string' || !payload.accountId || typeof payload.deviceId !== 'string' || !payload.deviceId) throw new Error('The entitlement account or device identity is missing.')
  if (payload.platform !== identity.platform) throw new Error('The entitlement belongs to another platform.')
  if (identity.platform !== 'web' && payload.deviceFingerprint !== identity.fingerprint) {
    throw new Error('The entitlement belongs to another device.')
  }
  return payload
}

/**
 * Activates (or renews) this device's certificate and applies what it grants.
 * Fails with `NO_LICENSE` when the account has never been licensed.
 */
export async function prepareEntitlement(accessToken: string, sessionId?: string, options: { signal?: AbortSignal; accountId?: string } = {}): Promise<{ certificate: CertificateEnvelope; payload: CertificatePayload }> {
  const identity = await deviceIdentity()
  let deviceId = await secureStorage.getActivationDeviceId()
  throwIfAborted(options.signal)
  const result = await withRequestDeadline(async (signal) => {
    const request = () => fetch(`${syncEngine.getApiUrl()}/api/devices/activate`, {
      method: 'POST', signal,
      headers: { 'content-type': 'application/json', authorization: `Bearer ${accessToken}` },
      body: JSON.stringify({ deviceId, sessionId, platform: identity.platform, deviceFingerprint: identity.fingerprint, label: identity.label }),
    })
    let response = await request()
    throwIfAborted(signal)
    if (response.status === 403 && deviceId) {
      // A failed retry must not discard the last working local identity.
      deviceId = null
      response = await request()
      throwIfAborted(signal)
    }
    if (!response.ok) {
      const body: unknown = await response.json().catch(() => null)
      const error = body && typeof body === 'object' ? body as { error?: string; code?: string } : {}
      if (error.code === 'NO_LICENSE') {
        throw new Error('This account doesn’t have a Superelectric Ink license yet. Buy one on the website, or ask whoever gave you access.')
      }
      throw new Error(error.code || error.error || `Activation failed (${response.status})`)
    }
    return await response.json() as { deviceId: string; certificate: CertificateEnvelope }
  }, { signal: options.signal })
  const payload = await verify(result.certificate, identity)
  if (options.accountId && payload.accountId !== options.accountId) throw new Error('The entitlement belongs to another account.')
  if (payload.deviceId !== result.deviceId) throw new Error('The entitlement device identity does not match.')
  throwIfAborted(options.signal)
  return { certificate: result.certificate, payload }
}

export async function persistEntitlement(prepared: Awaited<ReturnType<typeof prepareEntitlement>>, signal?: AbortSignal): Promise<void> {
  throwIfAborted(signal)
  if (typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window) {
    await getPlatformBridge().storeActivationCertificate(prepared.certificate)
  }
  throwIfAborted(signal)
  await secureStorage.setActivationCertificate(JSON.stringify(prepared.certificate))
  await secureStorage.setActivationDeviceId(prepared.payload.deviceId)
  throwIfAborted(signal)
}

export async function activateEntitlement(accessToken: string, sessionId?: string, options: { signal?: AbortSignal; accountId?: string } = {}): Promise<void> {
  const prepared = await prepareEntitlement(accessToken, sessionId, options)
  const owner = await secureStorage.getLocalAccountId()
  if (owner && owner !== prepared.payload.accountId) throw new OtherAccountWritingError(await secureStorage.getUsername())
  await persistEntitlement(prepared, options.signal)
  syncEngine.setSyncPaidUntil(toEntitlement(prepared.payload).syncUntil)
}

/** Select only a cryptographically verified copy, including native recovery after WebView cache loss. */
async function storedCertificate(): Promise<{ certificate: CertificateEnvelope; payload: CertificatePayload } | null> {
  const identity = await deviceIdentity()
  const primary = await secureStorage.getActivationCertificate()
  const backup = await secureStorage.getActivationCertificateBackup()
  const native = typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window
    ? await getPlatformBridge()?.readActivationCertificate?.().catch(() => null) : null
  const owner = await secureStorage.getLocalAccountId()
  for (const candidate of [primary, native, backup]) {
    if (!candidate) continue
    try {
      const certificate = typeof candidate === 'string' ? JSON.parse(candidate) as CertificateEnvelope : candidate as CertificateEnvelope
      const payload = await verify(certificate, identity)
      if (owner && payload.accountId !== owner) continue
      // Storage repair is best effort; a valid native/recovery copy still grants local use.
      if (candidate !== primary) {
        await secureStorage.setActivationCertificate(JSON.stringify(certificate)).catch(() => {})
        await secureStorage.setActivationDeviceId(payload.deviceId).catch(() => {})
      }
      if (typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window && JSON.stringify(native) !== JSON.stringify(certificate)) {
        await getPlatformBridge().storeActivationCertificate(certificate).catch(() => {
          window.dispatchEvent(new CustomEvent('auth:storage-error'))
        })
      }
      return { certificate, payload }
    } catch { /* Try the next complete copy. */ }
  }
  return null
}

export async function readOfflineAccountId(): Promise<string | null> {
  return (await storedCertificate())?.payload.accountId ?? null
}

/**
 * Reads this device's stored certificate without the network, and applies its
 * sync end date. Null when there is no valid certificate for this device.
 */
export async function readOfflineEntitlement(accountId?: string): Promise<OfflineEntitlement | null> {
  try {
    const stored = await storedCertificate()
    if (!stored) return null
    const { payload } = stored
    if (accountId && payload.accountId !== accountId) return null
    const entitlement = toEntitlement(payload)
    syncEngine.setSyncPaidUntil(entitlement.syncUntil)
    return entitlement
  } catch {
    return null
  }
}

export async function clearLocalEntitlement(): Promise<void> {
  await secureStorage.clearActivationCertificate()
  if (typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window) {
    await getPlatformBridge().deleteActivationCertificate()
  }
}
